/**
 * @aquilixshop/ui-react — the DEFAULT THEME ("premium-calm").
 *
 * Two complete, attractive page documents — a HOME (`index`) and a PRODUCT page — assembled ENTIRELY
 * from the section library's first preset (`presets[0]`), via {@link instantiateSection}. Nothing here
 * hand-writes settings: every section is seeded from its own validated preset, so the theme can never
 * drift from a section's default look. Each preset already ships demo content (headings, blocks, sample
 * reviews / FAQs / trust chips), and the product-grid / collection-list sections fall back to demo
 * products / collections when no picker resolves — so BOTH documents render fully with ZERO backend.
 *
 * WHY HERE (not in apps/builder or apps/storefront): the document model + the section registry both live
 * in this package, and BOTH apps (builder editor + storefront preview) need the same seed. Co-locating it
 * with the registry means "add a section to the default theme" is a one-line edit next to the registry,
 * and there is a single source of truth for the theme the builder opens to. The builder consumes these as
 * its offline/default draft (no backend); the storefront can use them as its demo home/product.
 *
 * SSR-safe: pure data assembly (no DOM, no effects). Documents are built fresh per call so callers never
 * share mutable section instances (each `instantiateSection` mints new nanoids).
 */
import { chromeDocumentOf, splitChromeSections, stripChromeSections, } from '../../document/chrome.js';
import { instantiateSection } from '../../document/hydrate.js';
import { getDefaultSectionRegistry } from '../../renderer/default-section-registry.js';
import { buildThankYouPageDocument } from '../../sections/_shared/thankYouPage.js';
import { buildWelcomePopupDocument } from '../../sections/_shared/welcomePopupPage.js';
import { buildCheckoutPageDocument } from '../../sections/_shared/checkoutPage.js';
import { AVERNOA_HOME_DOCUMENT } from './avernoaHome.js';
import { ATLAS_PRODUCT_DOCUMENT } from './atlasProduct.js';
/**
 * Build one section instance from a registry type's default preset. Throws if the type is unknown.
 *
 * THE LOCALE IS LOAD-BEARING (2026-09-13). A preset's starting sentences are `@t:` markers and field
 * `defaultKey`s resolved by `instantiateSection` against the store's language. Until this date the
 * locale was never passed, so the collection / cart / custom-page documents of the default theme were
 * seeded in FRENCH on every store, whatever its language — the same hole as the untranslated themes,
 * one level down.
 */
function seed(type, registry, locale) {
    const def = registry[type];
    if (!def)
        throw new Error(`default-theme: unknown section type "${type}".`);
    // presets[0] is the section's canonical default look — the SAME preset 'Add section' inserts.
    return instantiateSection(def, 0, locale);
}
function buildDocument(template, title, types, locale, registry) {
    return {
        schemaVersion: 1,
        template,
        handle: 'default',
        locale,
        meta: { title },
        sections: types.map((t) => seed(t, registry, locale)),
    };
}
/**
 * ONE HEADER FOR THE WHOLE STORE (founder call 2026-08-16). Every page document a theme ships is now
 * CHROME-FREE: the header / announcement bar / ticker / footer live in the theme's own `header` and
 * `footer` documents, seeded once per store and composed around every page by the storefront and by the
 * builder alike. Before this, each template carried a private copy — which is exactly how the home and
 * the product page ended up on two different headers (the Atlas product page still wore the nav of the
 * blivvy snapshot it was captured from), with no way to fix one from the other.
 */
function chromeFreeDocument(document) {
    return stripChromeSections(document);
}
/**
 * The HOME template's section order — the DOCUMENTED order of the real default home returned by
 * buildDefaultHomeDocument() (the avernoa clone that mirrors the backend default-home seed):
 *   slideshow → image-with-text → comparison-table → testimonials → contact-form
 *
 * This constant must stay in lock-step with AVERNOA_HOME_DOCUMENT (asserted by index.test.tsx and the
 * builder offline-fallback test). It documents what actually ships; it does NOT drive assembly.
 *
 * SCHEME RHYTHM: the page alternates rather than running flat — the hero photo, an INVERSE brand-story
 * image-with-text, a comparison-table, a light testimonials band, a contact-form, then the INVERSE footer.
 * That cadence is what makes the assembled page read as a finished premium store, not a stack of
 * same-coloured blocks.
 *
 * NO CHROME: the announcement bar / header / footer are NOT here any more. They are the store-wide
 * chrome, owned by {@link buildDefaultHeaderDocument} / {@link buildDefaultFooterDocument} and composed
 * around EVERY page — see `chromeFreeDocument`.
 */
export const HOME_SECTION_ORDER = [
    'slideshow',
    'image-with-text',
    'comparison-table',
    'testimonials',
    'contact-form',
];
/**
 * The PRODUCT template's section order — the DOCUMENTED order of the real default product page returned by
 * buildDefaultProductDocument(). Since 2026-07-26 that page is a verbatim snapshot (see
 * {@link ATLAS_PRODUCT_DOCUMENT}), NOT preset-assembled — so this constant DOCUMENTS what ships; it does NOT
 * drive assembly (asserted in lock-step by index.test.tsx):
 *   main-product → (section-divider → image-with-text) rhythm → content-tabs →
 *   results → comparison-table → image-with-text
 *
 * `main-product` composes the gallery (GalleryBody) + buy-box (BuyboxBody) side by side on desktop and leads
 * above the fold; the section-dividers + image-with-text bands + content-tabs + results + comparison-table
 * are the editorial body below it.
 *
 * NO CHROME (2026-08-16): the snapshot's own announcement bar ("-45% OFF SUMMER SALE") and header (the
 * blivvy "home / candida cleanse" nav) are stripped — THAT pair is what made the product page wear a
 * different header from the home. The store's one global header is composed around this body instead.
 */
export const PRODUCT_SECTION_ORDER = [
    'main-product',
    'section-divider',
    'image-with-text',
    'section-divider',
    'content-tabs',
    'section-divider',
    'image-with-text',
    'section-divider',
    'results',
    'comparison-table',
    'image-with-text',
];
/**
 * Default COLLECTION page — Shrine's collection template sequence: the collection banner (title +
 * description + image) then the product grid (with filters + sort). The global chrome is composed around
 * it, not listed here.
 */
export const COLLECTION_SECTION_ORDER = ['collection-banner', 'collection-grid'];
/** Default CART page — Shrine's cart template: the line items then the cart footer (summary + checkout). */
export const CART_SECTION_ORDER = ['cart-items', 'cart-footer'];
/**
 * The COLLECTIONS LIST template's section order (22/09/2026) — the `/collections` page, every collection of
 * the store in one `collection-list`. Until this template existed the storefront assembled that page from
 * code, so its colours could be edited nowhere.
 */
export const COLLECTIONS_SECTION_ORDER = ['collection-list'];
/**
 * Default CUSTOM PAGE (feature 0027 M1) — a blank canvas for the seller's own pages (About, FAQ, CGV…):
 * a single editable rich-text block, with the store's global chrome composed around it. Deliberately
 * minimal (not the HOME layout) so the seller fills it with their own sections. `rich-text` is
 * `page`-eligible (its schema lists `'page'`); the seller adds more sections from the Add-section picker.
 */
export const PAGE_SECTION_ORDER = ['rich-text'];
/**
 * Build the default HOME document. This is the founder's real avernoa home (announcement-bar → slideshow →
 * image-with-text → comparison-table → testimonials → contact-form, wrapped with header/footer) — the exact
 * clone the backend seeds (`DefaultStorefront.indexDoc` / default-home.fr.json), so the DEFAULT looks
 * identical everywhere the front falls back to it: the builder's demo/offline draft and the storefront's
 * 404 fallback. Returned as a fresh deep copy each call so callers never share a mutable document.
 * `locale`/`registry` are accepted for signature compatibility (the clone is authored in fr).
 */
export function buildDefaultHomeDocument(_locale = 'fr', _registry = getDefaultSectionRegistry()) {
    return chromeFreeDocument(structuredClone(AVERNOA_HOME_DOCUMENT));
}
/**
 * Build the default PRODUCT document — Atlas's product template, the exact page the founder built in the
 * builder (there is no separate named template for it any more). Returned as a fresh deep clone each call so
 * callers never share a mutable document. `locale`/`registry` are accepted for signature compatibility (the
 * snapshot is authored in fr, with fixed section instances).
 */
export function buildDefaultProductDocument(_locale = 'fr', _registry = getDefaultSectionRegistry()) {
    return chromeFreeDocument(structuredClone(ATLAS_PRODUCT_DOCUMENT));
}
/** Build the default COLLECTION document (fresh section instances each call). */
export function buildDefaultCollectionDocument(locale = 'fr', registry = getDefaultSectionRegistry()) {
    return buildDocument('collection', 'Collection page', [...COLLECTION_SECTION_ORDER], locale, registry);
}
/** Build the default COLLECTIONS LIST document (fresh section instances each call). */
export function buildDefaultCollectionsDocument(locale = 'fr', registry = getDefaultSectionRegistry()) {
    const doc = buildDocument('collections', 'Collections page', [...COLLECTIONS_SECTION_ORDER], locale, registry);
    // The list page shows EVERY collection, page-titled — the same settings the storefront hard-coded before
    // the template existed, so a store that never edits it renders exactly as it did.
    const list = doc.sections[0];
    if (list) {
        list.settings = {
            ...list.settings,
            headingSize: 'h1',
            source: 'all',
            columns: '4',
            columnsMobile: '2',
            maxCollections: 24,
            showViewAll: false,
            width: 'wide',
        };
    }
    return doc;
}
/** Build the default CART document (fresh section instances each call). */
export function buildDefaultCartDocument(locale = 'fr', registry = getDefaultSectionRegistry()) {
    return buildDocument('cart', 'Cart page', [...CART_SECTION_ORDER], locale, registry);
}
/**
 * Build the default CUSTOM PAGE document (feature 0027 M1) — the blank-canvas starter the builder sends when
 * the seller creates a new page. Fresh section instances each call.
 */
export function buildDefaultPageDocument(locale = 'fr', registry = getDefaultSectionRegistry()) {
    return buildDocument('page', 'Page', [...PAGE_SECTION_ORDER], locale, registry);
}
/**
 * Build the default GLOBAL HEADER document — the store-wide top chrome (announcement bar + header),
 * lifted VERBATIM from the avernoa home so a store that adopts the default theme keeps exactly the
 * header it has today; only its OWNERSHIP moves (from a copy inside each page to one document the whole
 * store shares). The Atlas product page's own header (the blivvy snapshot's "home / candida cleanse" nav
 * and its "-45% OFF SUMMER SALE" bar) is deliberately NOT a candidate: it was never the store's header,
 * it was the drift.
 */
export function buildDefaultHeaderDocument(locale = 'fr', _registry = getDefaultSectionRegistry()) {
    const home = { ...structuredClone(AVERNOA_HOME_DOCUMENT), locale };
    return chromeDocumentOf(home, 'header', 'default', splitChromeSections(home.sections).header);
}
/** Build the default GLOBAL FOOTER document — same source, same reasoning as the header. */
export function buildDefaultFooterDocument(locale = 'fr', _registry = getDefaultSectionRegistry()) {
    const home = { ...structuredClone(AVERNOA_HOME_DOCUMENT), locale };
    return chromeDocumentOf(home, 'footer', 'default', splitChromeSections(home.sections).footer);
}
/**
 * Resolve the default document for a template key. Falls back to HOME for any unseeded template so a
 * caller switching to e.g. 'search' still gets a renderable page (the home layout) rather than a crash —
 * the builder's switcher offers the seeded ones, but this stays total.
 *
 * EVERY TEMPLATE THE PAGE MENU OFFERS MUST HAVE A LINE HERE (23/09/2026). The home fallback is for keys
 * the seller cannot reach; for one they can, it is a trap. `thank-you` shipped without its line, so a
 * store whose `(thank-you, default)` row the backfill had not reached yet — every store on a machine where
 * the startup backfills are off — opened the thank-you page in the builder onto the HOME layout, while its
 * storefront painted the built-in confirmation. Two screens with nothing in common, and no way to tell
 * from the builder that it was showing a page that does not exist.
 */
export function buildDefaultDocument(template, locale = 'fr', registry = getDefaultSectionRegistry()) {
    if (template === 'product')
        return buildDefaultProductDocument(locale, registry);
    if (template === 'collection')
        return buildDefaultCollectionDocument(locale, registry);
    if (template === 'collections')
        return buildDefaultCollectionsDocument(locale, registry);
    if (template === 'cart')
        return buildDefaultCartDocument(locale, registry);
    // The SAME document the backend seeds and the storefront falls back to — one function, so the builder
    // cannot drift from the page a buyer is actually shown.
    if (template === 'thank-you')
        return buildThankYouPageDocument(locale);
    // Same contract as thank-you: ONE function, read by the backend seed, the builder and the storefront.
    if (template === 'checkout')
        return buildCheckoutPageDocument(locale);
    // La FENÊTRE DE BIENVENUE (0074 lot B). Même contrat : UNE fonction, lue par l'éditeur et par la
    // vitrine. Sans cette ligne, la fenêtre s'ouvrirait sur la mise en page de l'ACCUEIL — le piège exact
    // dans lequel la page de remerciement est tombée le 23/09, et que le garde-fou de `defaultTheme.test`
    // existe pour refuser.
    if (template === 'welcome-popup')
        return buildWelcomePopupDocument(locale);
    if (template === 'page')
        return buildDefaultPageDocument(locale, registry);
    return buildDefaultHomeDocument(locale, registry);
}
//# sourceMappingURL=index.js.map